import { useState, useEffect } from 'react';
import styles from './YearlyColor.module.css';
import apiService from '../services/api';
import ScrollReveal from '../components/Ui/ScrollReveal';

// HEX 색상을 RGB로 변환하는 함수
function hexToRgb(hex) {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16)
    } : null;
}

// 색상의 밝기를 계산하는 함수 (0-255)
function getBrightness(hex) {
    const rgb = hexToRgb(hex);
    if (!rgb) return 128;
    return (0.299 * rgb.r + 0.587 * rgb.g + 0.114 * rgb.b);
}

// RGB를 CMYK로 변환하는 함수
function rgbToCmyk(rgb) {
    if (!rgb) return { c: 0, m: 0, y: 0, k: 0 };

    let r = rgb.r / 255;
    let g = rgb.g / 255;
    let b = rgb.b / 255;

    let k = 1 - Math.max(r, g, b);
    let c = k === 1 ? 0 : (1 - r - k) / (1 - k);
    let m = k === 1 ? 0 : (1 - g - k) / (1 - k);
    let y = k === 1 ? 0 : (1 - b - k) / (1 - k);

    return {
        c: Math.round(c * 100),
        m: Math.round(m * 100),
        y: Math.round(y * 100),
        k: Math.round(k * 100)
    };
}

// HEX 색상을 대문자로 포맷팅
function formatHex(hex) {
    return hex.toUpperCase();
}

function YearlyColor() {
    const [yearlyColorData, setYearlyColorData] = useState({});
    const [selectedYear, setSelectedYear] = useState(null);
    const [showYearList, setShowYearList] = useState(false);
    const [loading, setLoading] = useState(true);
    const [colorValues, setColorValues] = useState({
        hex: '#000000',
        rgb: { r: 0, g: 0, b: 0 },
        cmyk: { c: 0, m: 0, y: 0, k: 0 }
    });

    // API에서 데이터 가져오기
    useEffect(() => {
        const fetchYearColorData = async () => {
            try {
                const response = await apiService.getYearColor();
                setYearlyColorData(response);
                
                // 연도 목록 추출 및 정렬 (최신순)
                const years = Object.keys(response).map(Number).sort((a, b) => b - a);
                if (years.length > 0) {
                    setSelectedYear(years[0]); // 가장 최신 연도 선택
                }
            } catch (error) {
                console.error('올해의 컬러 데이터 가져오기 실패:', error);
                setYearlyColorData({});
            } finally {
                setLoading(false);
            }
        };

        fetchYearColorData();
    }, []);

    const availableYears = Object.keys(yearlyColorData).map(Number).sort((a, b) => b - a);
    const currentData = selectedYear ? yearlyColorData[selectedYear] : null;

    useEffect(() => {
        if (currentData && currentData.hex) {
            const rgb = hexToRgb(currentData.hex);
            const cmyk = rgbToCmyk(rgb);

            setColorValues({
                hex: formatHex(currentData.hex),
                rgb: rgb || { r: 0, g: 0, b: 0 },
                cmyk: cmyk
            });
        }
    }, [currentData]);

    // 이전 연도로 이동
    const handlePrevYear = () => {
        const currentIndex = availableYears.indexOf(selectedYear);
        if (currentIndex < availableYears.length - 1) {
            setSelectedYear(availableYears[currentIndex + 1]);
        }
    };

    // 다음 연도로 이동
    const handleNextYear = () => {
        const currentIndex = availableYears.indexOf(selectedYear);
        if (currentIndex > 0) {
            setSelectedYear(availableYears[currentIndex - 1]);
        }
    };

    if (loading) {
        return (
            <div className={styles.yearlyColor}>
                <div className={styles.yearlyColorContainer}>
                    <div style={{ textAlign: 'center', padding: '100px' }}>
                        로딩 중...
                    </div>
                </div>
            </div>
        );
    }

    if (!currentData || availableYears.length === 0) {
        return (
            <div className={styles.yearlyColor}>
                <div className={styles.yearlyColorContainer}>
                    <div style={{ textAlign: 'center', padding: '100px' }}>
                        올해의 컬러 데이터가 없습니다.
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className={styles.yearlyColor}>
            <div className={styles.yearlyColorContainer}>
                <div className={styles.topText}>
                    <p className={styles.topTextTitle}>Color of the year</p>
                    <p className={styles.topTextSubtitle}>올해의 컬러</p>
                </div>

                {/* 연도 선택 네비게이션 */}
                <div className={styles.yearNavigation}>
                    <button
                        className={styles.navArrow}
                        onClick={handlePrevYear}
                        disabled={availableYears.indexOf(selectedYear) === availableYears.length - 1}
                    >
                       <img src="/image/year-color-nav-arrow-left.png" alt="yearly color nav arrow left" />
                    </button>

                    <div className={styles.yearSelector}>
                        <button
                            className={styles.yearButton}
                            onClick={() => setShowYearList(!showYearList)}
                        >
                            {selectedYear}
                        </button>

                        {showYearList && (
                            <div className={styles.yearDropdown}>
                                {availableYears.map(year => (
                                    <button
                                        key={year}
                                        className={`${styles.yearOption} ${year === selectedYear ? styles.active : ''}`}
                                        onClick={() => {
                                            setSelectedYear(year);
                                            setShowYearList(false);
                                        }}
                                    >
                                        {year}
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>

                    <button
                        className={styles.navArrow}
                        onClick={handleNextYear}
                        disabled={availableYears.indexOf(selectedYear) === 0}
                    >
                        <img src="/image/year-color-nav-arrow-right.png" alt="yearly color nav arrow right" />
                    </button>
                </div>

                <div className={styles.mainContent}>
                    <ScrollReveal>
                        <section className={styles.section01}>
                        <div className={styles.BeanerBox}>
                            <div className={styles.BeanerBoxInner}>
                                <img src={currentData.bannerImage || '/image/year-color-beaner-img.png'} alt="yearly color header" />
                            </div>
                            <div className={styles.colorCard}>
                                <div
                                    className={styles.color}
                                    style={{ background: currentData.hex }}
                                ></div>
                                <div className={styles.colorCardContent}>
                                    <div className={styles.colorCardContentTitle}>
                                        <p>{currentData.colorName}</p>
                                        <p>{selectedYear}</p>
                                    </div>
                                    <div className={styles.colorCardContentText}>
                                        <p>HEX<span>{colorValues.hex}</span></p>
                                        <p>RGB<span>{colorValues.rgb.r}, {colorValues.rgb.g}, {colorValues.rgb.b}</span></p>
                                        <p>CMYK<span>{colorValues.cmyk.c}, {colorValues.cmyk.m}, {colorValues.cmyk.y}, {colorValues.cmyk.k}</span></p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className={styles.mainText}>
                            <p className={styles.mainTextTitle}>
                                <span className={styles.mainTextTitleText}>{currentData.colorName}</span>
                                <span className={styles.mainTextKeyword}>{currentData.keyword}</span>
                            </p>
                            <p className={styles.mainTextSubtitle}>
                                {currentData.description}
                            </p>
                        </div>
                        <div className={styles.colorListBox}>
                            <img src={currentData.colorListImage || '/image/year-color-list-img.png'} alt="yearly color list" />
                        </div>
                    </section>
                    </ScrollReveal>
                    <ScrollReveal>
                        <section className={styles.section02}>
                        <div className={styles.imgbox}>
                            <img
                                src={currentData.beautySection?.image || '/image/education-section01-01.jpg'}
                                alt="컬러 이미지"
                            />
                        </div>
                        <div className={styles.infoBox}>
                            <div className={styles.infoHeader}>
                                <p className={styles.subtitle}>
                                    Beauty
                                </p>
                                <p className={styles.koTitle}>
                                    뷰티
                                </p>
                            </div>
                            <div className={styles.infoDetail}>
                                <div className={styles.infoDesc}>
                                    <span>
                                        {currentData.beautySection?.description || ''}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </section>
                    </ScrollReveal>
                    <ScrollReveal>
                        <section className={styles.section03}>
                        <div className={styles.imgbox}>
                            <img
                                src={currentData.fashionSection?.image || '/image/education-section01-01.jpg'}
                                alt="컬러 이미지"
                            />
                        </div>
                        <div className={styles.infoBox}>
                            <div className={styles.infoHeader}>
                                <p className={styles.subtitle}>
                                    Fashion
                                </p>
                                <p className={styles.koTitle}>
                                    패션
                                </p>
                            </div>
                            <div className={styles.infoDetail}>
                                <div className={styles.infoDesc}>
                                    <span>
                                        {currentData.fashionSection?.description || ''}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </section>
                    </ScrollReveal>
                    <ScrollReveal>
                        <section className={styles.section04}>
                        <div className={styles.imgbox}>
                            <img
                                src={currentData.interiorSection?.image || '/image/education-section01-01.jpg'}
                                alt="컬러 이미지"
                            />
                        </div>
                        <div className={styles.infoBox}>
                            <div className={styles.infoHeader}>
                                <p className={styles.subtitle}>
                                    Interior
                                </p>
                                <p className={styles.koTitle}>
                                    인테리어
                                </p>
                            </div>
                            <div className={styles.infoDetail}>
                                <div className={styles.infoDesc}>
                                    <span>
                                        {currentData.interiorSection?.description || ''}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </section>
                    </ScrollReveal>
                </div>
            </div>
        </div>
    );
}

export default YearlyColor;